import { useState, useRef, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Loader2, Camera, Send, MessagesSquare, TrendingUp, GitCompareArrows, Upload, Plus, CalendarRange } from "lucide-react";
import { toast } from "sonner";
import api, { API, formatEUR } from "@/lib/api";

const MODELS = [
  { id: "claude", label: "Claude Sonnet" },
  { id: "gemini-pro", label: "Gemini 3.1 Pro" },
  { id: "gemini-flash", label: "Gemini 3.8 Flash" },
];

// ----- Chat multi-turn -----
function ChatTab() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [model, setModel] = useState("claude");
  const scrollRef = useRef();

  const loadSessions = async () => {
    const { data } = await api.get("/ai/chat/sessions");
    setSessions(data);
  };
  useEffect(() => { loadSessions(); }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const loadSession = async (sid) => {
    const { data } = await api.get(`/ai/chat/sessions/${sid}`);
    setSessionId(sid);
    setMessages(data.messages || []);
    setModel(data.model || "claude");
  };

  const newChat = () => { setSessionId(null); setMessages([]); setInput(""); };

  const send = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput("");
    setMessages((m) => [...m, { role: "user", content: userMsg }]);
    setMessages((m) => [...m, { role: "assistant", content: "" }]);
    setLoading(true);
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: userMsg, session_id: sessionId, model }),
      });
      if (!res.body) throw new Error("no stream");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let acc = "";
      let newSid = sessionId;
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const line = p.replace(/^data:\s?/, "");
          if (line === "[DONE]") continue;
          const sm = line.match(/^\[SESSION_ID:(.+)\]$/);
          if (sm) { newSid = sm[1]; continue; }
          if (line.startsWith("[ERROR]")) { acc += "\n" + line; }
          else { acc += line; }
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "assistant", content: acc };
            return copy;
          });
        }
      }
      if (newSid && newSid !== sessionId) {
        setSessionId(newSid);
        loadSessions();
      }
    } catch (e) {
      toast.error("Erreur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid md:grid-cols-4 gap-4">
      <Card className="p-4 rounded-2xl border-stone-200 md:col-span-1 h-[600px] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-wider text-stone-500 font-medium">Discussions</div>
          <button onClick={newChat} className="text-xs text-stone-900 hover:underline" data-testid="chat-new-button">
            <Plus className="w-3 h-3 inline" /> Nouveau
          </button>
        </div>
        <div className="space-y-1">
          {sessions.length === 0 && <div className="text-xs text-stone-400 p-2">Aucune discussion</div>}
          {sessions.map((s) => (
            <button key={s.id} onClick={() => loadSession(s.id)}
                    data-testid={`chat-session-${s.id}`}
                    className={`w-full text-left px-3 py-2 rounded-xl text-sm truncate transition-colors ${
                      sessionId === s.id ? "bg-stone-100" : "hover:bg-stone-50"
                    }`}>
              {s.title || "Discussion"}
            </button>
          ))}
        </div>
      </Card>
      <Card className="rounded-2xl border-stone-200 md:col-span-3 flex flex-col h-[600px]">
        <div className="flex items-center justify-between p-4 border-b border-stone-100">
          <div className="text-sm font-semibold">Chat financier</div>
          <Select value={model} onValueChange={setModel}>
            <SelectTrigger className="w-40 h-8 rounded-lg text-xs" data-testid="chat-model-select"><SelectValue /></SelectTrigger>
            <SelectContent>{MODELS.map((m) => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
          {messages.length === 0 && (
            <div className="text-center text-stone-400 text-sm mt-16">
              <MessagesSquare className="w-10 h-10 mx-auto mb-2 text-stone-300" />
              Posez votre première question à l'IA financière
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm whitespace-pre-wrap ${
                m.role === "user"
                  ? "bg-stone-900 text-white rounded-tr-sm"
                  : "bg-amber-50 text-stone-800 border border-amber-100 rounded-tl-sm"
              }`}>
                {m.content || (loading && i === messages.length - 1 ? <span className="text-stone-400">...</span> : m.content)}
              </div>
            </div>
          ))}
        </div>
        <div className="p-3 border-t border-stone-100 flex gap-2">
          <Input value={input} onChange={(e) => setInput(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && send()}
                 placeholder="Posez une question..." disabled={loading}
                 className="rounded-xl" data-testid="chat-input" />
          <Button onClick={send} disabled={loading || !input.trim()}
                  className="rounded-xl bg-stone-900 hover:bg-stone-800" data-testid="chat-send-button">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </Card>
    </div>
  );
}

// ----- Compare Claude vs Gemini -----
function CompareTab() {
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/ai/compare", { question: question || null });
      setResult(data);
    } catch (e) {
      toast.error("Erreur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card className="p-6 rounded-2xl border-stone-200">
        <Input value={question} onChange={(e) => setQuestion(e.target.value)}
               placeholder="Votre question (laissez vide pour analyse générale)"
               className="rounded-xl h-11" data-testid="compare-question-input" />
        <div className="flex justify-end mt-3">
          <Button onClick={run} disabled={loading}
                  className="rounded-xl bg-stone-900 hover:bg-stone-800" data-testid="compare-run-button">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Comparaison...</>
                     : <><GitCompareArrows className="w-4 h-4 mr-2" /> Comparer</>}
          </Button>
        </div>
      </Card>

      {result && (
        <div className="grid md:grid-cols-2 gap-4">
          <Card className="p-5 rounded-2xl border-stone-200" data-testid="compare-claude-answer">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2 h-2 rounded-full bg-orange-500" />
              <div className="font-display font-semibold">Claude Sonnet 4.6</div>
              <div className="text-xs text-stone-500 ml-auto">Anthropic</div>
            </div>
            <div className="text-sm text-stone-800 whitespace-pre-wrap leading-relaxed">{result.claude}</div>
          </Card>
          <Card className="p-5 rounded-2xl border-stone-200" data-testid="compare-gemini-answer">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2 h-2 rounded-full bg-blue-500" />
              <div className="font-display font-semibold">Gemini 3.1 Pro</div>
              <div className="text-xs text-stone-500 ml-auto">Google</div>
            </div>
            <div className="text-sm text-stone-800 whitespace-pre-wrap leading-relaxed">{result.gemini}</div>
          </Card>
        </div>
      )}
    </div>
  );
}

// ----- Forecast -----
function ForecastTab() {
  const [answer, setAnswer] = useState("");
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setAnswer(""); setLoading(true);
    try {
      const res = await fetch(`${API}/ai/forecast`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = ""; let acc = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const line = p.replace(/^data:\s?/, "");
          if (line === "[DONE]") continue;
          if (line.startsWith("[ERROR]")) { acc += "\n" + line; continue; }
          acc += line;
          setAnswer(acc);
        }
      }
    } catch (e) {
      toast.error("Erreur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card className="p-6 rounded-2xl border-stone-200">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-display font-semibold text-lg">Prévision fin de mois</h3>
            <p className="text-sm text-stone-500 mt-1">Estimation IA de votre solde et risques de dépassement.</p>
          </div>
          <Button onClick={run} disabled={loading}
                  className="rounded-xl bg-stone-900 hover:bg-stone-800" data-testid="forecast-run-button">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyse...</>
                     : <><TrendingUp className="w-4 h-4 mr-2" /> Lancer la prévision</>}
          </Button>
        </div>
      </Card>
      {(answer || loading) && (
        <Card className="p-6 rounded-2xl border-blue-200 bg-blue-50/40" data-testid="forecast-answer">
          <div className="text-sm text-stone-800 whitespace-pre-wrap leading-relaxed">
            {answer || <span className="text-stone-400">Calcul en cours...</span>}
          </div>
        </Card>
      )}
    </div>
  );
}

// ----- Scan Receipt -----
function ScanTab() {
  const [loading, setLoading] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [accountId, setAccountId] = useState("");
  const fileRef = useRef();

  useEffect(() => {
    (async () => {
      const [a, c] = await Promise.all([api.get("/accounts"), api.get("/categories")]);
      setAccounts(a.data); setCategories(c.data);
      if (a.data[0]) setAccountId(a.data[0].id);
    })();
  }, []);

  const upload = async (file) => {
    setLoading(true); setExtracted(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/ai/scan-receipt", fd);
      setExtracted(data);
      toast.success("Ticket analysé");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'analyse");
    } finally { setLoading(false); }
  };

  const save = async () => {
    if (!extracted || !accountId) return;
    setSaving(true);
    try {
      const catId = extracted.category_id || categories.find((c) => c.type === "expense")?.id;
      await api.post("/transactions", {
        type: "expense",
        amount: parseFloat(extracted.amount),
        description: extracted.description,
        account_id: accountId,
        category_id: catId,
        date: extracted.date,
      });
      toast.success("Transaction créée");
      setExtracted(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) {
      toast.error("Erreur");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <Card className="p-8 rounded-2xl border-2 border-dashed border-stone-300 text-center">
        <Camera className="w-10 h-10 text-stone-400 mx-auto mb-3" />
        <h3 className="font-display font-semibold text-lg mb-1">Prendre en photo un ticket</h3>
        <p className="text-sm text-stone-500 mb-4">Gemini extrait automatiquement le montant, la date et la catégorie.</p>
        <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden"
               onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
               data-testid="scan-file-input" />
        <Button onClick={() => fileRef.current?.click()} disabled={loading}
                className="rounded-xl bg-stone-900 hover:bg-stone-800"
                data-testid="scan-upload-button">
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyse IA...</>
                   : <><Upload className="w-4 h-4 mr-2" /> Choisir / Prendre une photo</>}
        </Button>
      </Card>

      {extracted && (
        <Card className="p-6 rounded-2xl border-stone-200" data-testid="scan-result">
          <div className="text-xs uppercase tracking-wider text-emerald-600 font-medium mb-3">
            Données extraites par Gemini
          </div>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <div className="text-xs text-stone-500">Marchand</div>
              <div className="font-semibold">{extracted.merchant || "—"}</div>
            </div>
            <div>
              <div className="text-xs text-stone-500">Montant</div>
              <div className="font-semibold font-mono-num text-rose-600 text-xl">{formatEUR(extracted.amount)}</div>
            </div>
            <div>
              <div className="text-xs text-stone-500">Date</div>
              <div className="font-semibold">{extracted.date}</div>
            </div>
            <div>
              <div className="text-xs text-stone-500">Catégorie</div>
              <div className="font-semibold">{extracted.category_name || "—"}</div>
            </div>
          </div>
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <label className="text-xs text-stone-500 font-medium">Compte</label>
              <Select value={accountId} onValueChange={setAccountId}>
                <SelectTrigger className="mt-1 rounded-xl h-10" data-testid="scan-account-select"><SelectValue /></SelectTrigger>
                <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button onClick={save} disabled={saving}
                    className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="scan-save-button">
              {saving ? "Enregistrement..." : "Créer la transaction"}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

// ----- Advice (existing, kept minimal) -----
function AdviceTab() {
  const [q, setQ] = useState("");
  const [ans, setAns] = useState("");
  const [loading, setLoading] = useState(false);
  const [model, setModel] = useState("claude");

  const ask = async () => {
    setAns(""); setLoading(true);
    try {
      const res = await fetch(`${API}/ai/advice`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: q || null, model }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = ""; let acc = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const line = p.replace(/^data:\s?/, "");
          if (line === "[DONE]") continue;
          if (line.startsWith("[ERROR]")) { acc += "\n" + line; continue; }
          acc += line;
          setAns(acc);
        }
      }
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-4">
      <Card className="p-6 rounded-2xl border-stone-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-4">
          {MODELS.map((m) => (
            <button key={m.id} onClick={() => setModel(m.id)}
                    data-testid={`advice-model-${m.id}`}
                    className={`px-4 py-2.5 rounded-xl border text-sm font-medium transition-colors ${
                      model === m.id ? "border-stone-900 bg-stone-50" : "border-stone-200"
                    }`}>
              {m.label}
            </button>
          ))}
        </div>
        <Input value={q} onChange={(e) => setQ(e.target.value)}
               placeholder="Votre question ou laissez vide pour une analyse générale"
               className="rounded-xl h-11" data-testid="advice-question-input" />
        <div className="flex justify-end mt-3">
          <Button onClick={ask} disabled={loading}
                  className="rounded-xl bg-amber-500 hover:bg-amber-600 text-white"
                  data-testid="advice-ask-button">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyse...</>
                     : <><Sparkles className="w-4 h-4 mr-2" /> Demander conseil</>}
          </Button>
        </div>
      </Card>
      {(ans || loading) && (
        <Card className="p-6 rounded-2xl border-amber-200 bg-amber-50/40" data-testid="advice-answer">
          <div className="text-sm text-stone-800 whitespace-pre-wrap leading-relaxed">
            {ans || <span className="text-stone-400">Analyse en cours...</span>}
          </div>
        </Card>
      )}
    </div>
  );
}

// ----- Annual Forecast (12 months) -----
function AnnualForecastTab() {
  const [answer, setAnswer] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setAnswer(""); setData(null); setLoading(true);
    try {
      const res = await fetch(`${API}/ai/forecast-annual`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = ""; let acc = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const line = p.replace(/^data:\s?/, "");
          if (line === "[DONE]") continue;
          if (line.startsWith("[DATA]")) {
            try { setData(JSON.parse(line.slice(6))); } catch {}
            continue;
          }
          if (line.startsWith("[ERROR]")) { acc += "\n" + line; continue; }
          acc += line;
          setAnswer(acc);
        }
      }
    } catch (e) {
      toast.error("Erreur");
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-4">
      <Card className="p-6 rounded-2xl border-stone-200">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-display font-semibold text-lg">Projection annuelle</h3>
            <p className="text-sm text-stone-500 mt-1">Estimation IA de votre épargne sur les 12 prochains mois.</p>
          </div>
          <Button onClick={run} disabled={loading}
                  className="rounded-xl bg-stone-900 hover:bg-stone-800" data-testid="annual-run-button">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyse...</>
                     : <><CalendarRange className="w-4 h-4 mr-2" /> Lancer la projection</>}
          </Button>
        </div>
      </Card>

      {data && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="annual-kpis">
          <Card className="p-4 rounded-2xl border-stone-200">
            <div className="text-xs uppercase tracking-wider text-stone-500 font-medium">Solde actuel</div>
            <div className="font-display text-xl font-bold font-mono-num mt-1">{formatEUR(data.total_balance)}</div>
          </Card>
          <Card className="p-4 rounded-2xl border-stone-200">
            <div className="text-xs uppercase tracking-wider text-stone-500 font-medium">Épargne moy./mois</div>
            <div className={`font-display text-xl font-bold font-mono-num mt-1 ${data.avg_savings >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
              {data.avg_savings >= 0 ? "+" : ""}{formatEUR(data.avg_savings)}
            </div>
          </Card>
          <Card className="p-4 rounded-2xl border-stone-200">
            <div className="text-xs uppercase tracking-wider text-stone-500 font-medium">Revenus moy.</div>
            <div className="font-display text-xl font-bold font-mono-num mt-1 text-emerald-600">{formatEUR(data.avg_income)}</div>
          </Card>
          <Card className="p-4 rounded-2xl border-blue-200 bg-blue-50/50">
            <div className="text-xs uppercase tracking-wider text-blue-700 font-medium">Projection 12 mois</div>
            <div className="font-display text-xl font-bold font-mono-num mt-1 text-blue-700">{formatEUR(data.projected_12m)}</div>
          </Card>
        </div>
      )}

      {(answer || loading) && (
        <Card className="p-6 rounded-2xl border-blue-200 bg-blue-50/40" data-testid="annual-answer">
          <div className="text-sm text-stone-800 whitespace-pre-wrap leading-relaxed">
            {answer || <span className="text-stone-400">Calcul en cours...</span>}
          </div>
        </Card>
      )}
    </div>
  );
}

export default function AIAdvice() {
  return (
    <div className="space-y-6">
      <div>
        <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 rounded-full px-3 py-1 text-xs font-medium mb-3">
          <Sparkles className="w-3 h-3" /> Assistant financier IA
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Intelligence artificielle</h1>
        <p className="text-stone-500 mt-1">Conseils, chat, prévisions, comparaison et lecture de tickets.</p>
      </div>

      <Tabs defaultValue="advice" className="w-full">
        <TabsList className="grid grid-cols-6 w-full max-w-4xl rounded-xl bg-stone-100 p-1 h-auto">
          <TabsTrigger value="advice" className="rounded-lg text-xs md:text-sm py-2" data-testid="tab-advice">
            <Sparkles className="w-3.5 h-3.5 mr-1 hidden sm:inline" /> Conseil
          </TabsTrigger>
          <TabsTrigger value="chat" className="rounded-lg text-xs md:text-sm py-2" data-testid="tab-chat">
            <MessagesSquare className="w-3.5 h-3.5 mr-1 hidden sm:inline" /> Chat
          </TabsTrigger>
          <TabsTrigger value="forecast" className="rounded-lg text-xs md:text-sm py-2" data-testid="tab-forecast">
            <TrendingUp className="w-3.5 h-3.5 mr-1 hidden sm:inline" /> Fin mois
          </TabsTrigger>
          <TabsTrigger value="annual" className="rounded-lg text-xs md:text-sm py-2" data-testid="tab-annual">
            <CalendarRange className="w-3.5 h-3.5 mr-1 hidden sm:inline" /> 12 mois
          </TabsTrigger>
          <TabsTrigger value="compare" className="rounded-lg text-xs md:text-sm py-2" data-testid="tab-compare">
            <GitCompareArrows className="w-3.5 h-3.5 mr-1 hidden sm:inline" /> Comparer
          </TabsTrigger>
          <TabsTrigger value="scan" className="rounded-lg text-xs md:text-sm py-2" data-testid="tab-scan">
            <Camera className="w-3.5 h-3.5 mr-1 hidden sm:inline" /> Ticket
          </TabsTrigger>
        </TabsList>
        <TabsContent value="advice" className="mt-6"><AdviceTab /></TabsContent>
        <TabsContent value="chat" className="mt-6"><ChatTab /></TabsContent>
        <TabsContent value="forecast" className="mt-6"><ForecastTab /></TabsContent>
        <TabsContent value="annual" className="mt-6"><AnnualForecastTab /></TabsContent>
        <TabsContent value="compare" className="mt-6"><CompareTab /></TabsContent>
        <TabsContent value="scan" className="mt-6"><ScanTab /></TabsContent>
      </Tabs>
    </div>
  );
}
